import { MasterdataService } from '@app/services/admin/masterdata.service';
import { CommonModule } from '@angular/common';
import { Component, output } from '@angular/core';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import { TranslatePipe } from '@ngx-translate/core';
import { DetailsTabComponent } from '../person-role-details/details-tab/details-tab.component';
import { LocalizationTabComponent } from '../person-role-details/localization-tab/localization-tab.component';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  DatabaseManagementFormGroup,
  MergeLanguagesWithoutType,
} from '@app/models/form.models';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericDataService } from '@app/services/generic-data.service';
import { map, of } from 'rxjs';
import { DatabaseSaveService } from '@app/services/admin/database/database-save.service';
import { EntityKey } from '@app/models/admin.model';

@Component({
  selector: 'app-person-role-add',
  imports: [
    CommonModule,
    TranslatePipe,
    DetailsPageHeaderComponent,
    DetailsTabComponent,
    LocalizationTabComponent,
    ButtonComponent,
  ],
  templateUrl: './person-role-add.component.html',
  styleUrl: './person-role-add.component.scss',
})
export class PersonRoleAddComponent {
  public emitCancel = output<void>();
  public emitSave = output<void>();

  databaseManagementFormGroup: FormGroup<DatabaseManagementFormGroup>;
  private databaseSelector: EntityKey = 'personRole';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private service: GenericDataService,
    private saveSvc: DatabaseSaveService,
    private masterdataService: MasterdataService
  ) {
    const localizationTabForm = this.fb.group<{
      [language: string]: FormGroup<MergeLanguagesWithoutType>;
    }>({});
    this.databaseManagementFormGroup = this.fb.group({
      details: this.fb.nonNullable.group({
        code: this.fb.nonNullable.control('', {
          validators: [Validators.required, Validators.maxLength(10)],
          asyncValidators: [this.uniqueCodeValidator(() => null)],
          updateOn: 'blur',
        }),
        description: this.fb.control('', [Validators.required]),
      }),
      localizationsTab: localizationTabForm,
    });
  }

  uniqueCodeValidator(getOriginalCode?: () => string | null): AsyncValidatorFn {
    return (control: AbstractControl) => {
      const value = (control.value ?? '').toString().trim();

      if (!value || (getOriginalCode && getOriginalCode() === value)) {
        return of(null);
      }

      return this.masterdataService.loadPersonRolesCodeFiltered(value).pipe(
        map((res: any) => {
          const exists = (res?.data ?? []).some(
            (t: any) =>
              (t?.code ?? '').toString().toLowerCase() === value.toLowerCase()
          );
          return exists ? ({ codeTaken: true } as ValidationErrors) : null;
        })
      );
    };
  }

  ngOnInit(): void {
    this.service.loadLanguages().subscribe((langs) => {
      const formGroup = this.fb.group<{
        [language: string]: FormGroup<MergeLanguagesWithoutType>;
      }>({});

      for (const item of langs) {
        const isEN = item.code === 'EN';
        formGroup.addControl(
          item.code,
          this.fb.group({
            localizedName: this.fb.nonNullable.control(
              '',
              isEN ? [Validators.required] : []
            ),
          })
        );
      }

      this.databaseManagementFormGroup.setControl(
        'localizationsTab',
        formGroup
      );
    });
  }

  goBack(): void {
    const target =
      history.state?.from || `/admin/database/${this.databaseSelector}`;
    this.router.navigate([target], { relativeTo: this.route });
  }

  save() {
    if (this.databaseManagementFormGroup.invalid) {
      this.databaseManagementFormGroup.markAllAsTouched();
      this.databaseManagementFormGroup.updateValueAndValidity({
        emitEvent: true,
      });
      return;
    }
    this.saveSvc.saveFromForms(this.databaseManagementFormGroup, {
      entity: this.databaseSelector,
    });
  }
}
